import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { EnterpriseLayout } from "@/components/layout/EnterpriseLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Gift, Plus, Search, Package, DollarSign,
  Trash2, X, Wallet, CheckCircle, Clock,
  AlertCircle, RefreshCw, UserPlus, Phone, Mail,
  ChevronDown, ChevronUp, Filter, Coins, Zap
} from "lucide-react";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const USD_TO_HTG_RATE = 148;

// ===== MERU STATUS BADGE =====
const MeruBadge = ({ status }) => {
  const cfg = {
    pending_payment: { bg: "bg-yellow-100 text-yellow-800 border-yellow-200", label: "Tann Peman", icon: Clock },
    payment_received: { bg: "bg-blue-100 text-blue-800 border-blue-200", label: "Peman Resevwa", icon: CheckCircle },
    completed: { bg: "bg-green-100 text-green-800 border-green-200", label: "Rechaje ✅", icon: CheckCircle },
    cancelled: { bg: "bg-red-100 text-red-800 border-red-200", label: "Anile", icon: X },
  };
  const c = cfg[status] || { bg: "bg-gray-100 text-gray-600 border-gray-200", label: status, icon: AlertCircle };
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border ${c.bg}`}>
      <Icon className="w-3 h-3" />
      {c.label}
    </span>
  );
};

// ===== CRYPTO STATUS BADGE =====
const CryptoBadge = ({ status }) => {
  const cfg = {
    pending_payment: { bg: "bg-yellow-100 text-yellow-800 border-yellow-200", label: "Tann Peman", icon: Clock },
    payment_received: { bg: "bg-blue-100 text-blue-800 border-blue-200", label: "Peman Resevwa", icon: CheckCircle },
    completed: { bg: "bg-green-100 text-green-800 border-green-200", label: "Voye ✅", icon: CheckCircle },
    cancelled: { bg: "bg-red-100 text-red-800 border-red-200", label: "Anile", icon: X },
  };
  const c = cfg[status] || { bg: "bg-gray-100 text-gray-600 border-gray-200", label: status, icon: AlertCircle };
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border ${c.bg}`}>
      <Icon className="w-3 h-3" />
      {c.label}
    </span>
  );
};

// ===== MERU RECHARGE PANEL =====
const MeruRechargePanel = () => {
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/meru/admin/requests`);
      setRequests(data.requests || []);
      setStats(data.stats || {});
    } catch (e) {
      toast.error("Erè nan chaje demann rechaj Meru yo");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRequests(); }, []);

  const handleConfirmPayment = async (id, name) => {
    setActionLoading(id + "_pay");
    try {
      await axios.post(`${API}/meru/admin/requests/${id}/confirm-payment`, { notes: "Peman MonCash konfime" });
      toast.success(`Peman ${name} konfime!`);
      fetchRequests();
    } catch (e) {
      toast.error("Erè nan konfime peman");
    } finally { setActionLoading(null); }
  };

  const handleMarkRecharged = async (id, name) => {
    const ref = prompt(`Referans rechaj Meru pou ${name} (opsyonèl):`);
    setActionLoading(id + "_recharge");
    try {
      await axios.post(`${API}/meru/admin/requests/${id}/mark-recharged`, { meru_ref: ref || "", notes: "Kat Meru rechaje avèk siksè" });
      toast.success(`Kat Meru ${name} rechaje!`);
      fetchRequests();
    } catch (e) {
      toast.error("Erè nan make kòm rechaje");
    } finally { setActionLoading(null); }
  };

  const handleCancel = async (id) => {
    if (!window.confirm("Anile demann sa a?")) return;
    setActionLoading(id + "_cancel");
    try {
      await axios.post(`${API}/meru/admin/requests/${id}/cancel`, { reason: "Anile pa admin" });
      toast.success("Demann anile");
      fetchRequests();
    } catch (e) {
      toast.error("Erè nan anile demann");
    } finally { setActionLoading(null); }
  };

  const filtered = requests.filter(r => {
    const matchStatus = filterStatus === "all" || r.status === filterStatus;
    const matchSearch = !searchTerm ||
      r.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.client_phone?.includes(searchTerm) ||
      r.request_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.meru_email?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchStatus && matchSearch;
  });

  const formatDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleDateString("fr-HT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="space-y-5">
      {/* Meru Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Total", value: stats.total || 0, color: "bg-blue-50 text-blue-700", icon: Wallet },
          { label: "Tann Peman", value: stats.pending_payment || 0, color: "bg-yellow-50 text-yellow-700", icon: Clock },
          { label: "À Rechaje", value: stats.payment_received || 0, color: "bg-purple-50 text-purple-700", icon: AlertCircle },
          { label: "Konplè", value: stats.completed || 0, color: "bg-green-50 text-green-700", icon: CheckCircle },
        ].map(({ label, value, color, icon: Icon }) => (
          <div key={label} className={`rounded-2xl p-4 ${color} border border-opacity-20`}
            style={{ borderColor: "currentColor" }}>
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-bold uppercase tracking-wider opacity-70">{label}</p>
              <Icon className="w-4 h-4 opacity-60" />
            </div>
            <p className="text-3xl font-black">{value}</p>
          </div>
        ))}
      </div>

      {/* Revenue */}
      {stats.total_usd_recharged > 0 && (
        <div className="rounded-2xl p-4 flex items-center justify-between"
          style={{ background: "linear-gradient(135deg, #0d2b0d 0%, #1B6B1B 100%)" }}>
          <div>
            <p className="text-green-300 text-xs font-bold uppercase tracking-wider">Total Rechaje (USD)</p>
            <p className="text-3xl font-black text-white">${stats.total_usd_recharged?.toFixed(2)}</p>
          </div>
          <DollarSign className="w-12 h-12 text-green-400 opacity-40" />
        </div>
      )}

      {/* Filters + Search */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Chèche pa non, telefòn, imèl, oswa nimewo demann..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="pl-10 h-10 rounded-xl border-gray-200"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {[
            { key: "all", label: "Tout" },
            { key: "pending_payment", label: "Tann Peman" },
            { key: "payment_received", label: "À Rechaje" },
            { key: "completed", label: "Konplè" },
          ].map(f => (
            <button key={f.key} onClick={() => setFilterStatus(f.key)}
              className={`px-3 py-2 rounded-xl text-xs font-bold transition-all border ${filterStatus === f.key ? "bg-green-700 text-white border-green-700 shadow-md" : "bg-white text-gray-600 border-gray-200 hover:border-green-400"
                }`}>
              {f.label}
            </button>
          ))}
          <button onClick={fetchRequests}
            className="p-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-500 transition-all">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Requests List */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-10 h-10 rounded-full border-4 border-green-600 border-t-transparent animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-100 shadow-sm">
          <Wallet className="w-14 h-14 mx-auto text-gray-200 mb-3" />
          <p className="text-gray-500 font-semibold">Pa gen demann rechaj Meru</p>
          <p className="text-gray-400 text-sm mt-1">Kliyan yo ap soumèt demann depi dashbord yo</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(req => (
            <div key={req.id}
              className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-all">
              {/* Row header */}
              <div className="flex items-center justify-between p-4 cursor-pointer"
                onClick={() => setExpandedId(expandedId === req.id ? null : req.id)}>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-white font-black text-sm"
                    style={{ background: "linear-gradient(135deg, #1B6B1B, #2E8B2E)" }}>
                    {req.client_name?.charAt(0)?.toUpperCase() || "?"}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-black text-gray-800 text-sm truncate">{req.client_name}</p>
                      {req.needs_account_creation && (
                        <span className="flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700">
                          <UserPlus className="w-2.5 h-2.5" /> Nouvo kont
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-gray-400 font-mono">{req.request_number}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="text-right hidden sm:block">
                    <p className="font-black text-gray-800 text-sm">${req.amount_usd} USD</p>
                    <p className="text-[11px] text-gray-400">{parseInt(req.total_htg || 0).toLocaleString()} HTG</p>
                  </div>
                  <MeruBadge status={req.status} />
                  {expandedId === req.id ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </div>
              </div>

              {/* Expanded detail */}
              {expandedId === req.id && (
                <div className="border-t border-gray-100 p-4 space-y-4 bg-gray-50">
                  {/* Client info grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div className="bg-white rounded-xl p-3 border border-gray-100">
                      <p className="text-[9px] text-gray-400 uppercase font-bold tracking-wider mb-0.5 flex items-center gap-1">
                        <Phone className="w-3 h-3" /> Telefòn
                      </p>
                      <p className="text-sm font-black text-gray-800">{req.client_phone}</p>
                    </div>
                    {(req.meru_email || req.meru_phone) && (
                      <div className="bg-white rounded-xl p-3 border border-green-100">
                        <p className="text-[9px] text-green-600 uppercase font-bold tracking-wider mb-0.5 flex items-center gap-1">
                          <Wallet className="w-3 h-3" /> Kont Meru
                        </p>
                        <p className="text-sm font-black text-gray-800 truncate">{req.meru_email || req.meru_phone}</p>
                      </div>
                    )}
                    <div className="bg-white rounded-xl p-3 border border-gray-100">
                      <p className="text-[9px] text-gray-400 uppercase font-bold tracking-wider mb-0.5">Dat</p>
                      <p className="text-xs font-bold text-gray-700">{formatDate(req.created_at)}</p>
                    </div>
                  </div>

                  {/* Amount breakdown */}
                  <div className="bg-white rounded-xl p-3 border border-gray-100">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Rechaj Meru</span><span className="font-bold">${req.amount_usd}</span>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Frè sèvis</span><span className="font-bold">${req.fee_usd}</span>
                    </div>
                    <div className="flex justify-between text-xs font-black text-gray-800 border-t border-gray-100 pt-1 mt-1">
                      <span>Total USD</span><span>${req.total_usd}</span>
                    </div>
                    <div className="flex justify-between text-sm font-black text-green-700 mt-0.5">
                      <span>Total HTG (@ {req.exchange_rate})</span>
                      <span>{parseInt(req.total_htg || 0).toLocaleString()} HTG</span>
                    </div>
                  </div>

                  {/* Notes */}
                  {req.admin_notes && (
                    <p className="text-xs text-gray-500 italic bg-white rounded-xl p-3 border border-gray-100">
                      📝 {req.admin_notes}
                    </p>
                  )}

                  {/* Action buttons based on status */}
                  <div className="flex gap-2 flex-wrap">
                    {req.status === "pending_payment" && (
                      <Button
                        size="sm"
                        className="font-bold rounded-xl text-white"
                        style={{ background: "linear-gradient(135deg, #1B6B1B, #2E8B2E)" }}
                        onClick={() => handleConfirmPayment(req.id, req.client_name)}
                        disabled={actionLoading === req.id + "_pay"}
                      >
                        <CheckCircle className="w-4 h-4 mr-1.5" />
                        {actionLoading === req.id + "_pay" ? "Ap trete..." : "Peman MonCash Resevwa ✓"}
                      </Button>
                    )}

                    {req.status === "payment_received" && (
                      <Button
                        size="sm"
                        className="font-bold rounded-xl text-black"
                        style={{ background: "linear-gradient(135deg, #7eff7e, #4ade80)" }}
                        onClick={() => handleMarkRecharged(req.id, req.client_name)}
                        disabled={actionLoading === req.id + "_recharge"}
                      >
                        <Wallet className="w-4 h-4 mr-1.5" />
                        {actionLoading === req.id + "_recharge" ? "Ap trete..." : "Mark Kat Meru Rechaje ✅"}
                      </Button>
                    )}

                    {!["completed", "cancelled"].includes(req.status) && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-xl font-bold"
                        onClick={() => handleCancel(req.id)}
                        disabled={actionLoading === req.id + "_cancel"}
                      >
                        <X className="w-4 h-4 mr-1" />
                        Anile
                      </Button>
                    )}

                    {req.status === "completed" && req.recharged_at && (
                      <span className="flex items-center gap-1 text-green-700 text-xs font-bold">
                        <CheckCircle className="w-4 h-4" />
                        Rechaje le: {formatDate(req.recharged_at)}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// ===== CRYPTO PANEL =====
const CryptoPanel = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/crypto/admin/requests`);
      setRequests(data || []);
    } catch (e) {
      toast.error("Erè nan chaje demann USDT yo");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRequests(); }, []);

  const handleConfirmCompletion = async (id, name) => {
    const txHash = prompt(`Transaksyon Hash (TXID) pou ${name}:`);
    if (!txHash) return;

    setActionLoading(id + "_complete");
    try {
      await axios.post(`${API}/crypto/admin/requests/${id}/mark-completed`, { tx_hash: txHash });
      toast.success(`Demann ${name} fin trete!`);
      fetchRequests();
    } catch (e) {
      toast.error("Erè nan finalize demann");
    } finally { setActionLoading(null); }
  };

  const filtered = requests.filter(r => {
    const matchSearch = !searchTerm ||
      r.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.client_phone?.includes(searchTerm) ||
      r.wallet_address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.request_number?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchSearch;
  });

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          placeholder="Chèche pa non, telefòn, wallet, oswa nimewo..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="pl-10 h-10 rounded-xl"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-8 h-8 rounded-full border-4 border-green-600 border-t-transparent animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
          <Coins className="w-12 h-12 mx-auto text-gray-200 mb-2" />
          <p className="text-gray-500 font-bold">Pa gen demann USDT</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(req => (
            <div key={req.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="p-4 flex items-center justify-between cursor-pointer" onClick={() => setExpandedId(expandedId === req.id ? null : req.id)}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white" style={{ background: "linear-gradient(135deg, #111, #26A17B)" }}>
                    <Zap className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-black text-sm">{req.client_name}</p>
                    <p className="text-[10px] text-gray-400 font-mono">{req.request_number}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="font-black text-sm">${req.amount_usd} USDT</p>
                    <p className="text-[10px] text-gray-400">{parseInt(req.total_htg || 0).toLocaleString()} HTG</p>
                  </div>
                  <CryptoBadge status={req.status} />
                  {expandedId === req.id ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </div>
              </div>

              {expandedId === req.id && (
                <div className="px-4 pb-4 space-y-3 bg-gray-50">
                  <div className="p-3 bg-white rounded-xl border border-gray-100">
                    <p className="text-[9px] text-gray-400 font-bold uppercase">Wallet USDT TRC20</p>
                    <p className="text-xs font-mono break-all font-bold text-gray-800">{req.wallet_address}</p>
                  </div>
                  <div className="flex gap-2">
                    {req.status === "payment_received" && (
                      <Button size="sm" className="bg-[#26A17B] hover:bg-[#1E8262] text-white font-bold rounded-xl" onClick={() => handleConfirmCompletion(req.id, req.client_name)}>
                        Mark kòm Voye (Mete TXID)
                      </Button>
                    )}
                    {req.tx_hash && (
                      <div className="p-2.5 bg-green-50 rounded-xl border border-green-100 text-[10px] flex-1">
                        <span className="font-bold text-green-800">TXID:</span> <span className="font-mono text-green-700">{req.tx_hash}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// ===== MAIN COMPONENT =====
export const GiftcardAdmin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [giftCards, setGiftCards] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "giftcards"); // "giftcards" | "meru" | "crypto"
  const [showAddCodeModal, setShowAddCodeModal] = useState(false);

  useEffect(() => {
    const tab = searchParams.get("tab") || "giftcards";
    setActiveTab(tab);
  }, [searchParams]);

  // New code form state
  const [newCode, setNewCode] = useState({ code: "", amount_usd: "", price_htg: "" });
  const [exchangeRate, setExchangeRate] = useState(USD_TO_HTG_RATE);

  const token = localStorage.getItem('enterprise_token');
  const user = JSON.parse(localStorage.getItem('enterprise_user') || '{}');

  useEffect(() => {
    if (!token) { navigate('/enterprise'); return; }
    if (!['super_admin', 'giftcard_admin'].includes(user.role)) {
      toast.error("Ou pa gen aksè pou jere kat kado");
      navigate('/enterprise/dashboard');
      return;
    }
    fetchData();
  }, [token, navigate, user.role]);

  const fetchData = async () => {
    try {
      const cardsRes = await axios.get(`${API}/gift-cards/all`);
      const cards = cardsRes.data || [];
      setGiftCards(cards);
      setInventory(cards);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Erè nan chaje done yo");
    } finally {
      setLoading(false);
    }
  };

  const handleAmountChange = (value) => {
    const amount = parseFloat(value) || 0;
    setNewCode({ ...newCode, amount_usd: value, price_htg: (amount * exchangeRate).toFixed(0) });
  };

  const handleRateChange = (value) => {
    const rate = parseFloat(value) || USD_TO_HTG_RATE;
    setExchangeRate(rate);
    const amount = parseFloat(newCode.amount_usd) || 0;
    setNewCode({ ...newCode, price_htg: (amount * rate).toFixed(0) });
  };

  const handleAddCode = async (e) => {
    e.preventDefault();
    if (!newCode.code || !newCode.amount_usd) { toast.error("Ranpli tout chan yo"); return; }
    try {
      const response = await axios.post(`${API}/gift-cards`, {
        code: newCode.code,
        denomination: parseInt(newCode.amount_usd),
        price_htg: parseInt(newCode.price_htg)
      });
      if (response.data) {
        toast.success("Kòd kat kado ajoute avèk siksè!");
        setShowAddCodeModal(false);
        setNewCode({ code: "", amount_usd: "", price_htg: "" });
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erè nan ajoute kòd");
    }
  };

  const handleDeleteCode = async (codeId) => {
    if (!window.confirm("Ou sèten ou vle efase kòd sa a?")) return;
    try {
      await axios.delete(`${API}/gift-cards/${codeId}`);
      toast.success("Kòd efase");
      fetchData();
    } catch (error) {
      toast.error("Erè nan efase kòd");
    }
  };

  const filteredInventory = inventory.filter(item =>
    item.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.denomination?.toString().includes(searchTerm)
  );

  const availableCount = inventory.filter(i => !i.is_sold).length;
  const soldCount = inventory.filter(i => i.is_sold).length;

  return (
    <EnterpriseLayout>
      <div className="space-y-6" data-testid="giftcard-admin">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#232F3E]">Admin Kat Kado & Meru</h1>
            <p className="text-gray-500">Jere kat kado Amazon ak sèvis rechaj Meru</p>
          </div>
          {activeTab === "giftcards" && (
            <Button
              onClick={() => setShowAddCodeModal(true)}
              className="bg-[#FF9900] hover:bg-[#F08C00] text-black font-bold"
              data-testid="add-code-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Ajoute Yon Kat Kado
            </Button>
          )}
        </div>

        {/* Tab Switcher */}
        <div className="flex gap-2 bg-gray-100 rounded-2xl p-1.5 w-fit">
          <button
            onClick={() => {
              setActiveTab("giftcards");
              navigate("/enterprise/giftcards", { replace: true });
            }}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-black transition-all ${activeTab === "giftcards"
              ? "bg-white shadow-md text-[#232F3E]"
              : "text-gray-500 hover:text-gray-700"
              }`}
          >
            <Gift className="w-4 h-4" style={{ color: activeTab === "giftcards" ? "#FF9900" : undefined }} />
            Kat Kado Amazon
            <span className={`text-xs font-black px-1.5 py-0.5 rounded-lg ${activeTab === "giftcards" ? "bg-orange-100 text-orange-600" : "bg-gray-200 text-gray-400"}`}>
              {inventory.length}
            </span>
          </button>
          <button
            onClick={() => {
              setActiveTab("meru");
              navigate("/enterprise/giftcards?tab=meru", { replace: true });
            }}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-black transition-all ${activeTab === "meru"
              ? "bg-white shadow-md text-[#1B6B1B]"
              : "text-gray-500 hover:text-gray-700"
              }`}
          >
            <Wallet className="w-4 h-4" style={{ color: activeTab === "meru" ? "#1B6B1B" : undefined }} />
            Rechaj Meru
          </button>
          <button
            onClick={() => {
              setActiveTab("crypto");
              navigate("/enterprise/giftcards?tab=crypto", { replace: true });
            }}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-black transition-all ${activeTab === "crypto"
              ? "bg-white shadow-md text-[#26A17B]"
              : "text-gray-500 hover:text-gray-700"
              }`}
          >
            <Coins className="w-4 h-4" style={{ color: activeTab === "crypto" ? "#26A17B" : undefined }} />
            USDT
          </button>
        </div>

        {/* ===== GIFT CARDS TAB ===== */}
        {activeTab === "giftcards" && (
          <>
            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Kòd</p>
                      <p className="text-3xl font-bold text-[#232F3E]">{inventory.length}</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                      <Gift className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Disponib</p>
                      <p className="text-3xl font-bold text-green-600">{availableCount}</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                      <Package className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Vann</p>
                      <p className="text-3xl font-bold text-[#FF9900]">{soldCount}</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-orange-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Search */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    placeholder="Chèche pa kòd oswa montan..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="search-codes"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Inventory Table */}
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="loading-spinner" />
              </div>
            ) : filteredInventory.length === 0 ? (
              <Card className="border-0 shadow-lg">
                <CardContent className="p-12 text-center">
                  <Gift className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-xl font-semibold text-gray-600">Pa gen kòd</p>
                  <p className="text-gray-400">Ajoute premye kòd kat kado ou a</p>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-0 shadow-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">KÒD</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">MONTAN (USD)</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">PRI (HTG)</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">STATI</th>
                        <th className="px-6 py-4 text-right text-sm font-semibold text-gray-600">AKSYON</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredInventory.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50" data-testid={`code-${item.id}`}>
                          <td className="px-6 py-4">
                            <span className="font-mono font-medium text-[#232F3E]">{item.code}</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="font-bold text-[#FF9900]">${item.denomination}</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="font-medium">{(item.price_htg || item.denomination * USD_TO_HTG_RATE).toLocaleString()} HTG</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${!item.is_sold ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'}`}>
                              {!item.is_sold ? 'Disponib' : 'Vann'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            {!item.is_sold && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDeleteCode(item.id)}
                                data-testid={`delete-code-${item.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </>
        )}

        {/* ===== MERU TAB ===== */}
        {activeTab === "meru" && <MeruRechargePanel />}

        {/* ===== CRYPTO TAB ===== */}
        {activeTab === "crypto" && <CryptoPanel />}

        {/* Add Code Modal */}
        {showAddCodeModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowAddCodeModal(false)}>
            <Card className="w-full max-w-md bg-white" onClick={(e) => e.stopPropagation()}>
              <CardHeader className="border-b flex flex-row items-center justify-between">
                <CardTitle className="text-xl font-bold">Ajoute Yon Kat Kado</CardTitle>
                <Button variant="ghost" size="icon" onClick={() => setShowAddCodeModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </CardHeader>
              <CardContent className="p-6">
                <form onSubmit={handleAddCode} className="space-y-5">
                  <div className="space-y-2">
                    <Label className="text-base font-medium">Kòd Kat Kado</Label>
                    <Input
                      value={newCode.code}
                      onChange={(e) => setNewCode({ ...newCode, code: e.target.value.toUpperCase() })}
                      placeholder="AMZN-XXXX-XXXX-XXXX"
                      className="font-mono text-lg h-12 border-2 focus:border-[#FF9900]"
                      required data-testid="code-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-base font-medium">Montan (USD)</Label>
                    <Input
                      type="number" value={newCode.amount_usd}
                      onChange={(e) => handleAmountChange(e.target.value)}
                      placeholder="Antre montan (egz: 10, 25, 50, 100...)"
                      className="h-12 border-2 focus:border-[#FF9900]"
                      min="1" step="any" required data-testid="amount-input"
                    />
                    <p className="text-sm text-gray-500">Ou ka antre nenpòt montan ou vle</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-base font-medium">Pri (HTG)</Label>
                    <Input
                      type="number" value={newCode.price_htg}
                      onChange={(e) => setNewCode({ ...newCode, price_htg: e.target.value })}
                      placeholder="Pri an Goud"
                      className="h-12 border-2 focus:border-[#FF9900]"
                      min="1" required data-testid="price-input"
                    />
                    <p className="text-sm text-gray-500">
                      Tò otomatik: 1 USD = {exchangeRate} HTG{" "}
                      <button type="button" className="text-[#FF9900] ml-1 underline"
                        onClick={() => { const r = prompt("Antre nouvo tò:", exchangeRate.toString()); if (r) handleRateChange(r); }}>
                        (ou ka ajiste)
                      </button>
                    </p>
                  </div>
                  <Button type="submit"
                    className="w-full h-12 bg-[#FF9900] hover:bg-[#F08C00] text-black font-bold text-lg"
                    data-testid="submit-code-btn">
                    Ajoute
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </EnterpriseLayout>
  );
};

